import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Dialog, Select } from '@radix-ui/themes';
import { use, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DISCONNECT_LEVER, GET_LEVER_CONFIGS, UPDATE_LEVER_CONFIGS } from '../../../../behavior/queries/integration';
import LocaleContext from '../../../hooks/LocaleContext';

const LeverConfig = (props: {
  modelShow: boolean;
  modalHandleClose: () => void;
  updateIntegrationTable: (id: string) => void;
  setLeverIntegrationStatus: (status: boolean) => void;
}) => {
  const { modelShow, modalHandleClose, updateIntegrationTable, setLeverIntegrationStatus } = props;
  const { userSettings } = use(LocaleContext);
  const [loading, setLoading] = useState(false);
  const [{ leverLeadsTo, leverPassedTo, leverNotQualifiedTo }, setLeverConfig] = useState<{
    leverLeadsTo: string | undefined;
    leverPassedTo: string | undefined;
    leverNotQualifiedTo: string | undefined;
  }>({
    leverLeadsTo: undefined,
    leverPassedTo: undefined,
    leverNotQualifiedTo: undefined,
  });
  const [{ disconnectButtonLoading, saveButtonLoading }, setButtonLoading] = useState({
    disconnectButtonLoading: false,
    saveButtonLoading: false,
  });
  const [leverOptionArray, setLeverOptionArray] = useState<{ id: string; name: string }[]>([]);

  const [disconnectLever] = useMutation(DISCONNECT_LEVER);
  const [updateLeverConfigs] = useMutation(UPDATE_LEVER_CONFIGS);
  const [getLeverConfigs] = useLazyQuery(GET_LEVER_CONFIGS, {
    fetchPolicy: 'cache-and-network',
  });

  const tostError = () => {
    modalHandleClose();
    toast.error('Something went wrong. please try again', {
      duration: 5000,
    });
  };

  const fetchData = async (clientId: string) => {
    try {
      const gqlLeverConfigsResponse = await getLeverConfigs({
        variables: {
          clientId: clientId,
        },
      });
      if (
        gqlLeverConfigsResponse.data.clientIntegrations.Lever.stages &&
        gqlLeverConfigsResponse.data.clientIntegrations.Lever.stages.length < 0
      ) {
        tostError();
      }
      if (gqlLeverConfigsResponse.data.clientIntegrations.Lever.stages) {
        const tempOptionArray = [...gqlLeverConfigsResponse.data.clientIntegrations.Lever.stages];
        tempOptionArray.unshift({ id: 'null', name: 'None' });
        setLeverOptionArray(tempOptionArray);
      }
      if (gqlLeverConfigsResponse.data.clientIntegrations.Lever.config.__typename !== 'LeverConfig') {
        tostError();
      } else if (Object.keys(gqlLeverConfigsResponse.data.clientIntegrations.Lever.config.autoMove).length > 0) {
        setLeverConfig((state) => ({
          ...state,
          leverLeadsTo: gqlLeverConfigsResponse.data.clientIntegrations.Lever.config.autoMove.moveLeadsTo,
          leverPassedTo: gqlLeverConfigsResponse.data.clientIntegrations.Lever.config.autoMove.movePassedTo,
          leverNotQualifiedTo: gqlLeverConfigsResponse.data.clientIntegrations.Lever.config.autoMove.moveNotQualifiedTo,
        }));
      }
    } catch (error) {
      console.log(error);
      tostError();
    }
  };

  const saveData = async (clientId: string) => {
    try {
      setButtonLoading((state) => ({
        ...state,
        saveButtonLoading: true,
      }));
      const autoMove = {
        moveLeadsTo: leverLeadsTo === undefined || leverLeadsTo === null ? null : leverLeadsTo,
        movePassedTo: leverPassedTo === undefined || leverPassedTo === null ? null : leverPassedTo,
        moveNotQualifiedTo:
          leverNotQualifiedTo === undefined || leverNotQualifiedTo === null ? null : leverNotQualifiedTo,
      };

      const gqlUpdateLeverConfigResponse = await updateLeverConfigs({
        variables: {
          clientId: clientId,
          moveLeadsTo: autoMove.moveLeadsTo,
          movePassedTo: autoMove.movePassedTo,
          moveNotQualifiedTo: autoMove.moveNotQualifiedTo,
        },
      });
      if (gqlUpdateLeverConfigResponse.data.updateLeverConfig.__typename !== 'LeverConfig') {
        tostError();
      } else if (gqlUpdateLeverConfigResponse.data.updateLeverConfig.autoMove) {
        modalHandleClose();
        setButtonLoading((state) => ({
          ...state,
          saveButtonLoading: false,
        }));
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      tostError();
    }
  };

  const disconnectFromLever = async (clientId: string) => {
    try {
      setButtonLoading((state) => ({
        ...state,
        disconnectButtonLoading: true,
      }));
      const res = await disconnectLever({
        variables: {
          clientId: clientId,
        },
      });
      if (res.data.disconnectLever.__typename !== 'LeverDisconnectSuccess') {
        tostError();
      } else if (res.data.disconnectLever.success === 'OK') {
        setLeverIntegrationStatus(false);
        setButtonLoading((state) => ({
          ...state,
          disconnectButtonLoading: false,
        }));
        setLoading(false);
        if (userSettings.company) updateIntegrationTable(userSettings.company.id);
        modalHandleClose();
      }
    } catch (error) {
      console.log(error);
      tostError();
    }
  };

  useEffect(() => {
    setLoading(true);
    if (userSettings.company) fetchData(userSettings.company.id);
  }, [userSettings]);

  useEffect(() => {
    if (leverOptionArray.length > 0) {
      setLoading(false);
    }
  }, [leverOptionArray]);

  return (
    <Dialog.Root open={modelShow} onOpenChange={modalHandleClose}>
      <Dialog.Content aria-describedby={undefined}>
        <Dialog.Title>Integration settings</Dialog.Title>
        <div>
          <div className="flex justify-between items-center">
            <div className="lever-icon-lever-config-main">
              <img src="/integrationLogos/lever_rgb.svg" alt="Logo" className="lever-icon-lever-config-image" />
              <span className="lever-icon-lever-config-text">Lever</span>
            </div>
            <Button
              size="3"
              color="red"
              loading={loading || disconnectButtonLoading}
              className="mt-2 lever-config-modal-button"
              onClick={() => {
                if (userSettings.company) disconnectFromLever(userSettings.company.id);
              }}
            >
              Disconnect
            </Button>
          </div>
          <hr className="hr" />
          <div className="py-2">
            <span>Move Hubert Leads to</span>
            <Select.Root
              value={leverLeadsTo}
              disabled={loading}
              onValueChange={(newValue) =>
                setLeverConfig((state) => ({
                  ...state,
                  leverLeadsTo: newValue,
                }))
              }
            >
              <Select.Trigger className="w-full" />
              <Select.Content position="popper">
                {leverOptionArray.map((option) => (
                  <Select.Item key={option.id} value={option.id}>
                    {option.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>
          <div className="py-2">
            <span>Move Hubert passed to</span>
            <Select.Root
              value={leverPassedTo}
              disabled={loading}
              onValueChange={(newValue) =>
                setLeverConfig((state) => ({
                  ...state,
                  leverPassedTo: newValue,
                }))
              }
            >
              <Select.Trigger className="w-full" />
              <Select.Content position="popper">
                {leverOptionArray.map((option) => (
                  <Select.Item key={option.id} value={option.id}>
                    {option.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>
          <div className="py-2">
            <span>Move Hubert Not Qualified to</span>
            <Select.Root
              value={leverNotQualifiedTo}
              disabled={loading}
              onValueChange={(newValue) =>
                setLeverConfig((state) => ({
                  ...state,
                  leverNotQualifiedTo: newValue,
                }))
              }
            >
              <Select.Trigger className="w-full" />
              <Select.Content position="popper">
                {leverOptionArray.map((option) => (
                  <Select.Item key={option.id} value={option.id}>
                    {option.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>
        </div>

        <div className="flex">
          <div className="w-1/2 px-4">
            <Button
              color="grass"
              size="3"
              className="w-full"
              loading={loading || saveButtonLoading}
              onClick={() => {
                if (userSettings.company) saveData(userSettings.company.id);
              }}
            >
              Save
            </Button>
          </div>
          <div className="w-1/2 px-4">
            <Button size="3" variant="soft" color="gray" className="w-full" onClick={modalHandleClose}>
              Cancel
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default LeverConfig;
